/**
 * 资产知识库文件管理器
 * 负责管理资产知识库文件的业务操作
 * @author Joey
 */

import { useI18nNavigate } from "@/hooks/use-i18n-navigate";
import { useToast } from "@/hooks/use-toast";
import { canUseWindow } from "@/utils/canuseWindow";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { jsonStore } from "@/manager/cache/localdb";
import { ItemDetailData, knowledgeGetFolderItems } from "@/services/knowledge-get-folder-items";
import { assetsKnowledgeHooks } from "./assets-knowledge-manager";
import { useDebounce } from "@/hooks/use-debounce";
import { knowledgeUploadAttachment, KnowledgeUploadAttachmentData, KnowledgeUploadAttachmentRequest } from "@/services/knowledge-upload-attachment";
import { knowledgeItemDelete } from "@/services/knowledge-delete-folder-item";
import { useUploadController } from "@/hooks/use-upload-controller";
import { isAxiosError } from "axios";
import { knowledgeBatchDownload } from "@/services/knowledge-batch-download";
import { knowledgeUpdateFolderItemDetail } from "@/services/knowledge-update-folder-item-detail";
import { knowledgeCopyFolderItems, KnowledgeCopyFolderItemsParams } from "@/services/knowledge-copy-folder-items";
import { knowledgeMoveFolderItems, KnowledgeMoveFolderItemsParams } from "@/services/knowledge-move-folder-items";
import { useTranslation } from "react-i18next";

// 缓存数据结构
interface CacheData {
  knowledgeItems: ItemDetailData[];
}

type KnowledgeItemsChangeEventDetail = {
  knowledgeItems: ItemDetailData[];
};
type KnowledgeItemsChangeEvent = CustomEvent<KnowledgeItemsChangeEventDetail>;

class AssetsKnowledgeItemLocal {
  constructor(private storageKey: string) { }
  async get(id: string): Promise<CacheData | null> {
    return await jsonStore.get<CacheData>(`${this.storageKey}:${id}`);
  }
  async set(id: string, data: CacheData): Promise<void> {
    await jsonStore.set(`${this.storageKey}:${id}`, data);
  }
  async clear(): Promise<void> {
    await jsonStore.set(this.storageKey, null);
  }
}

class AssetsKnowledgeItemRemote {
  constructor(private cache: AssetsKnowledgeItemLocal) { }
  async getCached(id: string) {
    return await this.cache.get(id);
  }
  async fetchKnowledgeItems(folderId: number) {
    const data = await knowledgeGetFolderItems(folderId);
    return data.items || [];
  }
  async uploadKnowledgeItems({ files, folderId, onProgress, signal }: KnowledgeUploadAttachmentRequest): Promise<KnowledgeUploadAttachmentData> {
    return await knowledgeUploadAttachment({
      files,
      folderId,
      onProgress,
      signal,
    });
  }
  async deleteMaterialItems(ids: number[]) {
    return await knowledgeItemDelete(ids);
  }
  async updateKnowledgeItemDetail({ id, contentTitle }: { id: number, contentTitle: string }) {
    return await knowledgeUpdateFolderItemDetail({
      itemDetailId: id,
      contentTitle,
    });
  }
  async copyKnowledgeItems({ itemIds, targetFolderId }: KnowledgeCopyFolderItemsParams) {
    return await knowledgeCopyFolderItems({
      itemIds,
      targetFolderId,
    });
  }
  async moveKnowledgeItems({ itemIds, targetFolderId }: KnowledgeMoveFolderItemsParams) {
    return await knowledgeMoveFolderItems({
      itemIds,
      targetFolderId,
    });
  }
}

export class AssetsKnowledgeItemManager {
  private static instance: AssetsKnowledgeItemManager;
  private readonly STORAGE_KEY = "MIUTA_ASSETS_KNOWLEDGE_ITEM";
  private knowledgeItems: ItemDetailData[] = [];
  private cache: AssetsKnowledgeItemLocal;
  private api: AssetsKnowledgeItemRemote;

  private constructor() {
    this.cache = new AssetsKnowledgeItemLocal(this.STORAGE_KEY);
    this.api = new AssetsKnowledgeItemRemote(this.cache);
  }

  // 单例模式
  public static getInstance(): AssetsKnowledgeItemManager {
    if (!AssetsKnowledgeItemManager.instance) {
      AssetsKnowledgeItemManager.instance = new AssetsKnowledgeItemManager();
    }
    return AssetsKnowledgeItemManager.instance;
  }

  // 初始化
  public async init(folderId: number) {
    try {
      const cached = await this.api.getCached(folderId.toString());
      if (cached && cached.knowledgeItems) {
        this.setKnowledgeItems(folderId.toString(), cached.knowledgeItems);
        this.fetchAssetsKnowledgeItems(folderId);
        return;
      }
    } catch (error) {
      console.error("get cache data failed", error);
      this.setKnowledgeItems(folderId.toString(), []);
    }
    await this.fetchAssetsKnowledgeItems(folderId);
  }

  // 请求文件数据
  public async fetchAssetsKnowledgeItems(folderId: number) {
    try {
      const items = await this.api.fetchKnowledgeItems(folderId);
      this.setKnowledgeItems(folderId.toString(), items.map(item => {
        // 取最后一条记录
        const lastItemDetail = item.itemDetail?.[item.itemDetail.length - 1];
        return {
          id: lastItemDetail.id,
          parentId: item.id,
          itemType: item.itemType,
          contentTitle: lastItemDetail?.contentTitle,
          itemUrl: lastItemDetail?.itemUrl,
          createTime: lastItemDetail?.createTime,
          itemDetail: item.itemDetail,
        }
      }));
    } catch (error) {
      console.error("Failed to fetch assets knowledge item list:", error);
      this.dispatchKnowledgeItemsChange();
      throw error;
    }
  }

  // 上传文件
  public async knowledgeItemUpload({ files, folderId, onProgress, signal }: KnowledgeUploadAttachmentRequest) {
    try {
      return await this.api.uploadKnowledgeItems({ files, folderId, onProgress, signal });
    } catch (error) {
      console.error("Failed to upload assets knowledge item:", error);
      throw error;
    }
  }

  // 下载文件
  public async downloadKnowledgeItem(ids: number[], fileName?: string) {
    try {
      await knowledgeBatchDownload(ids, fileName);
    } catch (error) {
      console.error("Failed to download assets knowledge item:", error);
      throw error;
    }
  }

  // 删除文件
  public async knowledgeItemDelete(folderId: string, parentId: number[]) {
    const originKnowledge = [...this.knowledgeItems];
    try {
      // 乐观更新
      const knowledge = this.knowledgeItems.filter((item) => !parentId.includes(item.parentId));
      this.setKnowledgeItems(folderId, knowledge);

      await this.api.deleteMaterialItems(parentId);
    } catch (error) {
      this.setKnowledgeItems(folderId, originKnowledge);
      throw error;
    }
  }

  // 更新知识库卡片
  public async updateKnowledgeItemDetail(folderId: string, id: number, contentTitle: string) {
    const originKnowledgeItems = [...this.getKnowledgeItems()];
    try {
      // 乐观更新
      const knowledge = originKnowledgeItems.map(item => {
        if (item.id === id) {
          return { ...item, contentTitle };
        }
        return item;
      });
      this.setKnowledgeItems(folderId, knowledge);

      // 请求更新
      await this.api.updateKnowledgeItemDetail({ id, contentTitle });
    } catch (error) {
      this.setKnowledgeItems(folderId, originKnowledgeItems);
      console.error("Failed to update assets knowledge item detail:", error);
      throw error;
    }
  }

  // 复制知识库卡片
  public async copyKnowledgeItems({ itemIds, targetFolderId }: KnowledgeCopyFolderItemsParams) {
    try {
      return await this.api.copyKnowledgeItems({
        itemIds,
        targetFolderId,
      });
    } catch (error) {
      console.error("Failed to copy assets knowledge item:", error);
      throw error;
    }
  }

  // 移动知识库卡片
  public async moveKnowledgeItems({ itemIds, targetFolderId }: KnowledgeMoveFolderItemsParams) {
    try {
      return await this.api.moveKnowledgeItems({
        itemIds,
        targetFolderId,
      });
    } catch (error) {
      console.error("Failed to move assets knowledge item:", error);
      throw error;
    }
  }

  // ==================== 读取写入操作 ====================
  public getKnowledgeItems() {
    return this.knowledgeItems;
  }
  public setKnowledgeItems(folderId: string, list: ItemDetailData[]) {
    this.knowledgeItems = list;
    this.dispatchKnowledgeItemsChange();
    this.cache.set(folderId, { knowledgeItems: this.knowledgeItems });
  }

  // ==================== 订阅通知操作 ====================
  // 监听列表变化事件
  private dispatchKnowledgeItemsChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<KnowledgeItemsChangeEventDetail>(`${this.STORAGE_KEY}:list-change`, {
      detail: { knowledgeItems: this.knowledgeItems }
    });
    window.dispatchEvent(event);
  }
  public onKnowledgeItemsChange(listener: (e: KnowledgeItemsChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:list-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }
}

// ============= Hooks =============
const useKnowledgeItemsData = () => {
  const toast = useToast();
  const params = useParams();

  const [loading, setLoading] = useState(false);
  const [knowledgeItems, setKnowledgeItems] = useState(assetsKnowledgeItemManager.getKnowledgeItems());
  const { knowledgeFolders } = assetsKnowledgeHooks.useAssetsKnowledgeData();

  const fetchKnowledgeItems = async () => {
    try {
      setLoading(true);
      await assetsKnowledgeItemManager.init(Number(params.id));
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  const currentFolder = knowledgeFolders.find((item) => item.id === Number(params.id));

  // 防抖请求知识库数据
  const debouncedFetchKnowledgeItems = useDebounce(fetchKnowledgeItems, 400, {
    immediately: true,
  });

  useEffect(() => {
    debouncedFetchKnowledgeItems();
  }, [params.id]);

  useEffect(() => {
    const unsubscribe = assetsKnowledgeItemManager.onKnowledgeItemsChange((e) => {
      setKnowledgeItems(e.detail.knowledgeItems)
    })
    return unsubscribe
  }, [])

  return {
    knowledgeItems,
    loading,
    currentFolder,
  }
}

// 删除文件
const useKnowledgeItemDelete = () => {
  const { showToastByCode } = useToast();
  const [loading, setLoading] = useState(false);
  const params = useParams();

  const deleteKnowledgeItem = async (id: number[]) => {
    try {
      setLoading(true);
      await assetsKnowledgeItemManager.knowledgeItemDelete(params.id, id);
    } catch (error) {
      showToastByCode(error);
      throw new Error(error)
    } finally {
      setLoading(false);
    }
  };

  return {
    deleteKnowledgeItem,
    loading,
  };
};

// 上传文件
const useKnowledgeItemUpload = () => {
  const params = useParams();
  const { showToastByCode } = useToast();
  const { addUploadTasks } = useUploadController();

  const [loading, setLoading] = useState(false);

  const uploadKnowledgeItem = async (files: File[]) => {
    try {
      setLoading(true);

      addUploadTasks(files, async (file, onProgress, signal) => {
        try {
          return await assetsKnowledgeItemManager.knowledgeItemUpload({
            files: file,
            folderId: Number(params.id),
            onProgress,
            signal,
          });
        } catch (error) {
          if (isAxiosError(error) && error.code === 'ERR_CANCELED') throw error;
          console.error(error)
          showToastByCode(error);
          throw new Error(error)
        }
      }, {
        mode: 'parallel',
        concurrency: 5,
        onTaskComplete(task, result) {
          // 成功后更新列表
          if (result.results?.length > 0) {
            const detail = result.results[0]?.detail
            const item = result.results[0]?.item
            assetsKnowledgeItemManager.setKnowledgeItems(params.id, [
              {
                id: detail?.id,
                contentTitle: detail?.contentTitle,
                itemUrl: detail?.itemUrl,
                createTime: detail?.createTime,
                itemType: item?.itemType,
                parentId: item?.id,
              },
              ...assetsKnowledgeItemManager.getKnowledgeItems(),
            ]);
          }
        },
      })
    } catch (error) {
      showToastByCode(error);
      throw new Error(error)
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    uploadKnowledgeItem,
  };
};

// 下载文件
const useKnowledgeItemDownload = () => {
  const { showToastByCode } = useToast();
  const [loading, setLoading] = useState(false);

  const downloadKnowledgeItem = async (ids: number[], fileName?: string) => {
    try {
      setLoading(true);
      await assetsKnowledgeItemManager.downloadKnowledgeItem(ids, fileName);
    } catch (error) {
      showToastByCode(error);
      throw new Error(error)
    } finally {
      setLoading(false);
    }
  };

  return {
    downloadKnowledgeItem,
    loading,
  };
};

// 更新知识库卡片
const useKnowledgeItemUpdate = () => {
  const params = useParams();
  const { showToastByCode } = useToast();

  const [loading, setLoading] = useState(false);

  const updateKnowledgeItemDetail = async (id: number, contentTitle: string) => {
    try {
      setLoading(true);
      await assetsKnowledgeItemManager.updateKnowledgeItemDetail(params.id, id, contentTitle);
    } catch (error) {
      showToastByCode(error);
      throw new Error(error)
    } finally {
      setLoading(false);
    }
  };

  return {
    updateKnowledgeItemDetail,
    loading,
  };
};

// 复制知识库卡片
const useKnowledgeItemCopy = () => {
  const params = useParams();
  const { t } = useTranslation();
  const { showToastByCode, toast } = useToast();

  const [loading, setLoading] = useState(false);

  const copyKnowledgeItem = async (ids: number[], targetFolderId: number | number[]) => {
    try {
      setLoading(true);
      await assetsKnowledgeItemManager.copyKnowledgeItems({
        itemIds: ids,
        targetFolderId,
      });
      await assetsKnowledgeItemManager.fetchAssetsKnowledgeItems(Number(params.id));
      toast({
        description: t('assets.knowledge_item.move_modal.copy_success'),
      });
    } catch (error) {
      showToastByCode(error);
      throw new Error(error)
    } finally {
      setLoading(false);
    }
  };

  return {
    copyKnowledgeItem,
    loading,
  };
};

// 移动知识库卡片
const useKnowledgeItemMove = () => {
  const params = useParams();
  const { t } = useTranslation();
  const { showToastByCode, toast } = useToast();

  const [loading, setLoading] = useState(false);

  const moveKnowledgeItem = async (ids: number[], targetFolderId: number | number[]) => {
    try {
      setLoading(true);
      await assetsKnowledgeItemManager.moveKnowledgeItems({
        itemIds: ids,
        targetFolderId,
      });
      await assetsKnowledgeItemManager.fetchAssetsKnowledgeItems(Number(params.id));
      toast({
        description: t('assets.knowledge_item.move_modal.move_success'),
      });
    } catch (error) {
      showToastByCode(error);
      throw new Error(error)
    } finally {
      setLoading(false);
    }
  };

  return {
    moveKnowledgeItem,
    loading,
  };
};

// 获取知识库文件列表
const useFetchKnowledgeItems = () => {
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [knowledgeItems, setKnowledgeItems] = useState(assetsKnowledgeItemManager.getKnowledgeItems());

  const fetchKnowledgeItems = async (folderId: number) => {
    try {
      setLoading(true);
      await assetsKnowledgeItemManager.init(folderId)
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const unsubscribe = assetsKnowledgeItemManager.onKnowledgeItemsChange((e) => {
      setKnowledgeItems(e.detail.knowledgeItems)
    })
    return unsubscribe
  }, [])

  return {
    knowledgeItems,
    loading,
    fetchKnowledgeItems: useDebounce(fetchKnowledgeItems, 400, {
      immediately: true,
    }),
  }
}

export const assetsKnowledgeItemHooks = {
  useKnowledgeItemsData,
  useFetchKnowledgeItems,
  useKnowledgeItemDelete,
  useKnowledgeItemUpload,
  useKnowledgeItemDownload,
  useKnowledgeItemUpdate,
  useKnowledgeItemCopy,
  useKnowledgeItemMove,
}

// 导出单例实例
export const assetsKnowledgeItemManager = AssetsKnowledgeItemManager.getInstance();
